import React, { useState, useEffect } from 'react';
import {
  Modal,
  Card,
  Row,
  Col,
  Button,
  Space,
  Tag,
  Typography,
  Input,
  Select,
  Empty,
  message,
  Divider,
  Badge,
  Spin,
} from 'antd';
import {
  ShoppingOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  SearchOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import api from '../../services/authApi';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;

interface TemplateGalleryProps {
  visible: boolean;
  customerId?: number;
  onClose: () => void;
}

const TemplateGallery: React.FC<TemplateGalleryProps> = ({ visible, customerId, onClose }) => {
  const [templates, setTemplates] = useState<any[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>();
  const [selectedTemplate, setSelectedTemplate] = useState<any | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);

  useEffect(() => {
    if (visible) {
      fetchTemplates();
      fetchCategories();
    }
  }, [visible, categoryFilter]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (searchText) params.search = searchText;
      if (categoryFilter) params.category = categoryFilter;

      const response = await api.get('/public/estimate-templates', {
        params
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      message.error('Failed to load service packages');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/public/estimate-templates/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSelectTemplate = async (templateId: number) => {
    if (!customerId) {
      message.error('Please log in to select a service package');
      return;
    }

    try {
      const response = await api.post(
        `/public/estimate-templates/${templateId}/select`,
        { customer_id: customerId }
      );

      const { estimate, publicUrl } = response.data;

      message.success('Service package added to your estimates!');

      // Open the public estimate URL in a new tab
      if (publicUrl) {
        window.open(publicUrl, '_blank');
      }

      onClose();
    } catch (error: any) {
      console.error('Error selecting template:', error);
      const errorMessage = error?.response?.data?.error || 'Failed to select service package';
      message.error(errorMessage);
    }
  };

  const viewDetails = (template: any) => {
    setSelectedTemplate(template);
    setDetailsVisible(true);
  };

  const filteredTemplates = templates.filter(template => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return (
      template.template_name.toLowerCase().includes(searchLower) ||
      template.description?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <>
      <Modal
        title={
          <Space>
            <ShoppingOutlined />
            <Title level={4} style={{ margin: 0 }}>Service Packages</Title>
          </Space>
        }
        open={visible}
        onCancel={onClose}
        width={1200}
        footer={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ]}
        style={{ top: 20 }}
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Search and Filter */}
          <Card>
            <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
              <Search
                placeholder="Search service packages..."
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={fetchTemplates}
                style={{ width: 300 }}
                prefix={<SearchOutlined />}
              />
              <Select
                placeholder="All Categories"
                allowClear
                style={{ width: 200 }}
                value={categoryFilter}
                onChange={(value) => {
                  setCategoryFilter(value);
                }}
              >
                {categories.map(cat => (
                  <Select.Option key={cat} value={cat}>{cat}</Select.Option>
                ))}
              </Select>
            </Space>
          </Card>

          {/* Template Cards */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <Spin size="large" />
            </div>
          ) : filteredTemplates.length > 0 ? (
            <Row gutter={[16, 16]}>
              {filteredTemplates.map((template) => (
                <Col key={template.id} xs={24} sm={12} lg={8}>
                  <Badge.Ribbon
                    text={template.category}
                    color="blue"
                  >
                    <Card
                      hoverable
                      cover={
                        template.image_url ? (
                          <img
                            alt={template.template_name}
                            src={template.image_url}
                            style={{ height: 180, objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{
                            height: 180,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <ShoppingOutlined style={{ fontSize: 64, color: 'white', opacity: 0.8 }} />
                          </div>
                        )
                      }
                      actions={[
                        <Button
                          type="link"
                          icon={<SearchOutlined />}
                          onClick={() => viewDetails(template)}
                        >
                          Details
                        </Button>,
                        <Button
                          type="primary"
                          icon={<CheckCircleOutlined />}
                          onClick={() => handleSelectTemplate(template.id)}
                        >
                          Select
                        </Button>,
                      ]}
                    >
                      <Card.Meta
                        title={
                          <div>
                            <Text strong style={{ fontSize: 16 }}>{template.template_name}</Text>
                          </div>
                        }
                        description={
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Paragraph
                              ellipsis={{ rows: 2 }}
                              type="secondary"
                              style={{ marginBottom: 8, minHeight: 40 }}
                            >
                              {template.description || 'Professional service package'}
                            </Paragraph>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Space>
                                <ClockCircleOutlined />
                                <Text type="secondary">{template.estimated_duration_hours} hrs</Text>
                              </Space>
                              <Text strong style={{ fontSize: 18, color: '#52c41a' }}>
                                ${template.total_amount.toFixed(2)}
                              </Text>
                            </div>
                            {template.tags && template.tags.length > 0 && (
                              <div style={{ marginTop: 8 }}>
                                {template.tags.map((tag: string, idx: number) => (
                                  <Tag key={idx} style={{ marginBottom: 4 }}>{tag}</Tag>
                                ))}
                              </div>
                            )}
                          </Space>
                        }
                      />
                    </Card>
                  </Badge.Ribbon>
                </Col>
              ))}
            </Row>
          ) : (
            <Empty
              description="No service packages available"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Space>
      </Modal>

      {/* Template Details Modal */}
      <Modal
        title="Service Package Details"
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailsVisible(false)}>
            Close
          </Button>,
          <Button
            key="select"
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() => {
              if (selectedTemplate) {
                handleSelectTemplate(selectedTemplate.id);
                setDetailsVisible(false);
              }
            }}
          >
            Select This Package
          </Button>,
        ]}
        width={700}
      >
        {selectedTemplate && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Title level={4}>{selectedTemplate.template_name}</Title>
              <Space>
                <Tag color="blue">{selectedTemplate.category}</Tag>
                <Tag icon={<ClockCircleOutlined />}>
                  {selectedTemplate.estimated_duration_hours} hours
                </Tag>
                <Tag icon={<DollarOutlined />} color="green">
                  ${selectedTemplate.total_amount.toFixed(2)}
                </Tag>
              </Space>
            </div>

            {selectedTemplate.description && (
              <div>
                <Text strong>Description:</Text>
                <Paragraph style={{ marginTop: 8 }}>{selectedTemplate.description}</Paragraph>
              </div>
            )}

            {selectedTemplate.line_items && selectedTemplate.line_items.length > 0 && (
              <div>
                <Text strong>What's Included:</Text>
                <Card style={{ marginTop: 8 }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {selectedTemplate.line_items.map((item: any, idx: number) => (
                      <div key={idx}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <Text strong>{item.description}</Text>
                          <Text strong>${item.total_price.toFixed(2)}</Text>
                        </div>
                        {item.labor_hours > 0 && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Labor: {item.labor_hours} hrs
                          </Text>
                        )}
                        {item.parts && item.parts.length > 0 && (
                          <div style={{ marginLeft: 16, marginTop: 4 }}>
                            {item.parts.map((part: any, partIdx: number) => (
                              <div key={partIdx} style={{ fontSize: 12, color: '#8c8c8c' }}>
                                • {part.part_name} (${part.price.toFixed(2)})
                              </div>
                            ))}
                          </div>
                        )}
                        {idx < selectedTemplate.line_items.length - 1 && (
                          <Divider style={{ margin: '12px 0' }} />
                        )}
                      </div>
                    ))}
                  </Space>
                </Card>
              </div>
            )}

            <Card style={{ backgroundColor: '#f6ffed', borderColor: '#b7eb8f' }}>
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>Subtotal:</Text>
                  <Text>${(selectedTemplate.total_amount / (1 + selectedTemplate.tax_rate / 100)).toFixed(2)}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>Tax ({selectedTemplate.tax_rate}%):</Text>
                  <Text>${((selectedTemplate.total_amount / (1 + selectedTemplate.tax_rate / 100)) * (selectedTemplate.tax_rate / 100)).toFixed(2)}</Text>
                </div>
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong style={{ fontSize: 16 }}>Total:</Text>
                  <Text strong style={{ fontSize: 18, color: '#52c41a' }}>
                    ${selectedTemplate.total_amount.toFixed(2)}
                  </Text>
                </div>
              </Space>
            </Card>
          </Space>
        )}
      </Modal>
    </>
  );
};

export default TemplateGallery;
